/**
 * InventoryMenu - Player inventory UI showing items and allowing usage
 */

import React, { useState } from 'react';
import { inventorySystem, type ItemDefinition, type InventoryItem } from '../../game/systems/InventorySystem';
import { playUIClick, playUIHover } from '../../game/audio/SoundEffects';

interface InventoryMenuProps {
  visible?: boolean;
  onClose?: () => void;
}

export const InventoryMenu: React.FC<InventoryMenuProps> = ({
  visible = false,
  onClose
}) => {
  const [selectedTab, setSelectedTab] = useState<'all' | 'consumable' | 'key_item' | 'collectible'>('all');
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  if (!visible) return null;

  // Get inventory items
  const inventoryItems = inventorySystem.getInventory();
  const stats = inventorySystem.getStats();

  // Filter items by selected tab
  const filteredItems = selectedTab === 'all'
    ? inventoryItems
    : inventoryItems.filter(invItem => {
        const itemDef = inventorySystem.getItemDefinition(invItem.itemId);
        return itemDef?.category === selectedTab;
      });

  const handleUseItem = (itemId: string) => {
    playUIClick();
    const success = inventorySystem.useItem(itemId);
    if (success) {
      console.log(`✅ Successfully used item: ${itemId}`);
      // Force re-render by changing selected item
      setSelectedItem(null);
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'consumable': return '#00ff00';
      case 'key_item': return '#ffaa00';
      case 'collectible': return '#00aaff';
      default: return '#ffffff';
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.95)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      fontFamily: 'monospace',
      color: '#ffffff',
      zIndex: 2500,
      padding: '40px',
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '20px',
        textAlign: 'center',
      }}>
        <h1 style={{
          fontSize: '42px',
          fontWeight: 'bold',
          color: '#00aaff',
          textShadow: '0 0 20px #00aaff',
          marginBottom: '10px',
          letterSpacing: '4px',
        }}>
          INVENTORY
        </h1>
        <div style={{
          fontSize: '16px',
          color: '#aaa',
        }}>
          Slots: {stats.slotsUsed} / {stats.slotsUsed + stats.slotsAvailable} | Total Items: {stats.totalItems}
        </div>
      </div>

      {/* Category Tabs */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '20px',
      }}>
        {(['all', 'consumable', 'key_item', 'collectible'] as const).map((tab) => {
          const isSelected = selectedTab === tab;
          const labels = {
            all: 'ALL',
            consumable: 'CONSUMABLES',
            key_item: 'KEY ITEMS',
            collectible: 'COLLECTIBLES'
          };

          return (
            <button
              key={tab}
              onClick={() => {
                playUIClick();
                setSelectedTab(tab);
              }}
              onMouseEnter={() => playUIHover()}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 'bold',
                fontFamily: 'monospace',
                backgroundColor: isSelected ? 'rgba(0, 170, 255, 0.3)' : 'rgba(0, 0, 0, 0.5)',
                color: isSelected ? '#00aaff' : '#888',
                border: `2px solid ${isSelected ? '#00aaff' : '#444'}`,
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              {labels[tab]}
            </button>
          );
        })}
      </div>

      {/* Inventory Grid */}
      <div style={{
        width: '100%',
        maxWidth: '900px',
        height: '400px',
        overflowY: 'auto',
        marginBottom: '20px',
        padding: '20px',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        border: '2px solid #444',
        borderRadius: '8px',
      }}>
        {filteredItems.length === 0 ? (
          <div style={{
            textAlign: 'center',
            color: '#666',
            fontSize: '18px',
            marginTop: '100px',
          }}>
            No items in this category
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '15px',
          }}>
            {filteredItems.map((invItem) => {
              const itemDef = inventorySystem.getItemDefinition(invItem.itemId);
              if (!itemDef) return null;

              const isHovered = hoveredItem === invItem.itemId;
              const isSelected = selectedItem === invItem.itemId;
              const categoryColor = getCategoryColor(itemDef.category);

              return (
                <div
                  key={invItem.itemId}
                  onMouseEnter={() => {
                    setHoveredItem(invItem.itemId);
                    playUIHover();
                  }}
                  onMouseLeave={() => setHoveredItem(null)}
                  onClick={() => {
                    playUIClick();
                    setSelectedItem(invItem.itemId);
                  }}
                  style={{
                    padding: '15px',
                    backgroundColor: isHovered || isSelected
                      ? 'rgba(0, 170, 255, 0.2)'
                      : 'rgba(0, 0, 0, 0.7)',
                    border: `2px solid ${isHovered || isSelected ? categoryColor : '#444'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: isHovered ? `0 0 15px ${categoryColor}40` : 'none',
                  }}
                >
                  {/* Item Icon Placeholder */}
                  <div style={{
                    width: '50px',
                    height: '50px',
                    margin: '0 auto 10px',
                    backgroundColor: categoryColor,
                    border: '2px solid #000',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                  }}>
                    {itemDef.category === 'consumable' ? '🧪' :
                     itemDef.category === 'key_item' ? '🔑' : '📜'}
                  </div>

                  {/* Item Name */}
                  <div style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: categoryColor,
                    marginBottom: '5px',
                    textAlign: 'center',
                  }}>
                    {itemDef.name}
                  </div>

                  {/* Quantity */}
                  {itemDef.maxStack > 1 && (
                    <div style={{
                      fontSize: '12px',
                      color: '#aaa',
                      textAlign: 'center',
                      marginBottom: '8px',
                    }}>
                      x{invItem.quantity}
                    </div>
                  )}

                  {/* Description */}
                  <div style={{
                    fontSize: '11px',
                    color: '#888',
                    marginBottom: '10px',
                    textAlign: 'center',
                    lineHeight: '1.3',
                  }}>
                    {itemDef.description}
                  </div>

                  {/* Use Button (only for consumables) */}
                  {itemDef.consumable && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUseItem(invItem.itemId);
                      }}
                      style={{
                        width: '100%',
                        padding: '6px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        fontFamily: 'monospace',
                        backgroundColor: 'rgba(0, 255, 0, 0.2)',
                        color: '#00ff00',
                        border: '2px solid #00ff00',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(0, 255, 0, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(0, 255, 0, 0.2)';
                      }}
                    >
                      USE
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Close Button */}
      <button
        onClick={() => {
          playUIClick();
          onClose?.();
        }}
        style={{
          padding: '12px 40px',
          fontSize: '18px',
          fontWeight: 'bold',
          fontFamily: 'monospace',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          color: '#ffffff',
          border: '2px solid #ffffff',
          borderRadius: '6px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          letterSpacing: '2px',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(50, 50, 50, 0.9)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
        }}
      >
        CLOSE
      </button>
    </div>
  );
};
